import type { PrintProbe } from '@wehunt/ui'
import { apiFetch } from '../../../shared/api/apiFetch'

/** The Estate API's passthrough of Tanger's print probe, camelCased. */
interface PrintProbeResponse {
  status: 'SUCCESS' | 'UNAVAILABLE' | string
  crs: string | null
  proj4: string | null
  groundMPerCrsM: number | null
  baseMapFlavors: string[] | null
  propertyBorders: boolean | null
  capabilities?: string[] | null
}

/**
 * What the print server would use for a sheet centred at a point: the CRS
 * (with its proj4 definition), the ground factor, the flavours it prints there
 * and whether property borders can be drawn — or its own `UNAVAILABLE`.
 *
 * Goes through the Estate API rather than Tanger: web never talks to Tanger
 * directly, and the API's passthrough asks the same print configuration the
 * render uses, so the frame drawn on screen and the sheet the server composes
 * cannot disagree. A non-2xx rejects (through `apiFetch`), which the print
 * frame treats as "no answer" and keeps whatever it last knew.
 */
export async function fetchPrintProbe(
  latitude: number,
  longitude: number,
  token: string,
): Promise<PrintProbe> {
  const res = await apiFetch<PrintProbeResponse>(
    `/maps/print/probe?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}`,
    { token },
  )
  if (res.status === 'UNAVAILABLE') return { status: 'UNAVAILABLE' }
  if (res.status !== 'SUCCESS') throw new Error(`print probe: unexpected status ${res.status}`)

  // "EPSG:3067" → 3067. Anything else is not a CRS this client can load, and a
  // frame drawn from a guess would be wrong in a way the user cannot see.
  const match = /^EPSG:(\d+)$/.exec(res.crs ?? '')
  if (!match || !res.proj4 || typeof res.groundMPerCrsM !== 'number' || !res.baseMapFlavors) {
    throw new Error(`print probe: unreadable crs in ${JSON.stringify(res)}`)
  }
  return {
    status: 'SUCCESS',
    crs: { epsg: Number(match[1]), proj4: res.proj4, groundMPerCrsM: res.groundMPerCrsM },
    flavors: res.baseMapFlavors,
    propertyBorders: res.propertyBorders === true,
    capabilities: Array.isArray(res.capabilities) ? res.capabilities.filter((c): c is string => typeof c === 'string') : [],
  }
}
